import { API } from './amplifyCompat'

// Since sprint PR 8b a chat room is readable only by its members. Rows written before the chat
// memberIds follow-up can name a user who is not (or no longer) in their room's memberIds:
// anyone signed in could plant one, and a member could change a room's or a row's memberIds or
// repoint a row. Since that follow-up a new row needs its room's memberIds and its user among
// them, and none of these fields can change, but the old rows remain. A query that lists such a
// row with its chatRoom (getUser.chatRoomUser, doesChatRoomExist) then returns that chatRoom as
// null plus an Unauthorized error, and the Amplify client rejects the whole result: one planted
// row would stop the account (journals, books, chat) loading, or stop the user starting a chat.

const DENIAL = /unauthori[sz]ed/i

// An error that is exactly that: the chatRoom of one row of the list at `listPath` (e.g.
// ['getUser', 'chatRoomUser', 'items']), refused by authorization.
export function isUnreadableChatRoom(error, listPath = ['getUser', 'chatRoomUser', 'items']) {
  const path = error?.path
  return (
    Array.isArray(path) &&
    path.length === listPath.length + 2 &&
    listPath.every((p, i) => path[i] === p) &&
    Number.isInteger(path[listPath.length]) &&
    path[listPath.length + 1] === 'chatRoom' &&
    DENIAL.test(`${error.errorType ?? ''} ${error.message ?? ''}`)
  )
}

// True for a rejected API.graphql whose errors are all authorization refusals (the caller may
// not read that row); network, throttling and other errors are false.
export function isAuthorizationRefusal(err) {
  const errors = err?.errors
  return Array.isArray(errors) && errors.length > 0 && errors.every((e) => DENIAL.test(`${e?.errorType ?? ''} ${e?.message ?? ''}`))
}

const at = (data, path) => path.reduce((o, k) => o?.[k], data)

// API.graphql for a query that lists ChatRoomUser rows with their chatRoom at `listPath`: a
// result whose only errors are unreadable chat rooms in that list is kept, without those rows;
// any other error is thrown as before.
export async function graphqlSkippingUnreadableRooms(request, listPath) {
  let result
  try {
    result = await API.graphql(request)
  } catch (err) {
    const errors = err?.errors
    const parent = at(err?.data, listPath.slice(0, -1))
    if (!parent || !Array.isArray(errors) || !errors.length || !errors.every((e) => isUnreadableChatRoom(e, listPath))) throw err
    console.log(`${listPath[0]}: left out ${errors.length} chat room(s) the user can't read`)
    result = { data: err.data }
  }
  const parent = at(result?.data, listPath.slice(0, -1))
  const key = listPath[listPath.length - 1]
  if (Array.isArray(parent?.[key])) parent[key] = parent[key].filter((r) => r?.chatRoom)
  return result
}

// Every getUser document the web sends selects chatRoomUser.items.chatRoom.
export const graphqlGetUser = (request) => graphqlSkippingUnreadableRooms(request, ['getUser', 'chatRoomUser', 'items'])
